import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/gossip";

const CAT_LABEL: Record<string, { name: string; color: string }> = {
  zongyi: { name: "综艺名场面", color: "bg-primary-500" },
  yingshi: { name: "影视热映", color: "bg-accent-500" },
  yinyue: { name: "乐坛现场", color: "bg-secondary-500" },
  guancha: { name: "圈内观察", color: "bg-primary-700" },
};

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = articles.find((a) => a.id === id);
  const [up, setUp] = useState(0);
  const [liked, setLiked] = useState(false);
  const [toast, setToast] = useState(false);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(false), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (!item) {
    return (
      <>
        <Header />
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-ghost-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">瓜料走丢了</h1>
          <p className="mt-2 text-sm text-foreground-500">这条内容可能已下线，去首页看看今天的瓜单吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-home-4-line" aria-hidden="true"></i> 返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const catMeta = CAT_LABEL[item.category] || CAT_LABEL.zongyi;
  const related = articles
    .filter((a) => a.id !== item.id)
    .sort((a, b) => (a.category === item.category ? b.heat - a.heat : a.heat - b.heat))
    .slice(0, 3);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      /* ignore */
    }
    setToast(true);
  };

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: item.summary,
    image: item.image,
    datePublished: item.date,
    author: { "@type": "Organization", name: "海角吃瓜" },
    publisher: { "@type": "Organization", name: "海角吃瓜" },
    mainEntityOfPage: `https://wind-in-soul.cn/detail/${item.id}`,
    articleSection: catMeta.name,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://wind-in-soul.cn/" },
      { "@type": "ListItem", position: 2, name: catMeta.name, item: `https://wind-in-soul.cn/category/${item.category}` },
      { "@type": "ListItem", position: 3, name: item.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <Header />
      <main className="w-full bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 lg:px-12">
          {/* 面包屑 */}
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to={`/category/${item.category}`} className="hover:text-primary-600">{catMeta.name}</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-700">{item.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
            {/* 正文 */}
            <article className="lg:col-span-8" aria-label="文章正文">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-background-50 ${catMeta.color}`}>
                <i className="ri-fire-fill" aria-hidden="true"></i>
                热度 {item.heat} · {catMeta.name}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{item.summary}</p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200/70 py-4 text-sm text-foreground-500">
                <span className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-fire-fill" aria-hidden="true"></i>
                  </span>
                  海角吃瓜编辑部
                </span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line" aria-hidden="true"></i>{item.date}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{item.views} 阅读</span>
                <span className="flex items-center gap-1 whitespace-nowrap"><i className="ri-time-line" aria-hidden="true"></i>约 {item.readMinutes} 分钟</span>
              </div>

              {/* 头图 */}
              <figure className="mt-7">
                <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl bg-background-200">
                  <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
                </div>
                <figcaption className="mt-2 text-center text-xs text-foreground-400">
                  {item.tags[0]} · {item.title} 相关配图
                </figcaption>
              </figure>

              {/* 正文段落 */}
              <div className="mt-8 space-y-6 text-[15px] leading-[1.9] text-foreground-800 md:text-base">
                {item.content.map((p, i) => (
                  <p key={i} className={i === 0 ? "first-letter:float-left first-letter:mr-3 first-letter:font-heading first-letter:text-5xl first-letter:font-black first-letter:leading-none first-letter:text-primary-600" : ""}>
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-8 rounded-2xl border border-background-200/70 bg-secondary-100/60 p-5 text-sm leading-relaxed text-foreground-600">
                <p className="flex items-center gap-2 font-semibold text-foreground-800">
                  <i className="ri-information-line text-primary-600" aria-hidden="true"></i>
                  内容说明
                </p>
                <p className="mt-2">
                  本篇内容由海角吃瓜编辑部基于公开可核实的节目物料与行业信息整理编辑，旨在提供娱乐资讯与讨论参考。相关视频与物料请以官方平台为准，理性吃瓜、去伪存真。
                </p>
              </div>

              {/* 标签 */}
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">话题：</span>
                {item.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 border border-background-200 px-3 py-1 text-xs whitespace-nowrap text-foreground-600">
                    #{t}
                  </span>
                ))}
              </div>

              {/* 互动 */}
              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200/70 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setLiked((v) => !v);
                    setUp((n) => (liked ? n - 1 : n + 1));
                  }}
                  className={
                    "inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors " +
                    (liked
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300")
                  }
                  aria-pressed={liked}
                >
                  <i className="ri-heart-3-fill" aria-hidden="true"></i>
                  这瓜给力 {up > 0 && `+${up}`}
                </button>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-300"
                >
                  <i className="ri-share-forward-line" aria-hidden="true"></i>
                  分享给瓜友
                </button>
                <span className="ml-auto hidden items-center gap-1 text-sm text-foreground-400 sm:flex">
                  <i className="ri-bookmark-line" aria-hidden="true"></i> 已收录到吃瓜合集
                </span>
              </div>

              {toast && (
                <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-foreground-950 px-3 py-1.5 text-xs text-background-50" role="status">
                  <i className="ri-check-line" aria-hidden="true"></i> 链接已复制，快分享给朋友吧
                </p>
              )}
            </article>

            {/* 侧栏 */}
            <aside className="lg:col-span-4" aria-label="相关推荐">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-2xl border border-background-200/70 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                      <i className="ri-thumb-up-line text-xs" aria-hidden="true"></i>
                    </span>
                    瓜友可能也爱看
                  </h2>
                  <ul className="mt-4 space-y-1">
                    {related.map((a, idx) => (
                      <li key={a.id}>
                        <Link
                          to={`/detail/${a.id}`}
                          className="group flex items-start gap-3 rounded-lg px-2 py-2.5 hover:bg-background-200/70"
                        >
                          <span className="mt-0.5 w-5 shrink-0 font-heading text-lg font-black text-secondary-400">
                            {idx + 1}
                          </span>
                          <span className="min-w-0">
                            <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">
                              {a.title}
                            </span>
                            <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                              <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                              {a.heat} · {a.views} 阅读
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/category/${item.category}`}
                    className="mt-4 flex items-center justify-center gap-1 rounded-full border border-background-300 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    看更多{catMeta.name} <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* 相关卡片 */}
          <section className="mt-14">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-layout-masonry-fill text-sm" aria-hidden="true"></i>
              </span>
              相关阅读推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} item={a} />
              ))}
            </div>
          </section>
        </div>
      </main>

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-30 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-foreground-950 text-background-50 shadow-lg transition-transform hover:scale-105"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-lg" aria-hidden="true"></i>
        </button>
      )}
      <Footer />
    </>
  );
}